import { normalizeVi } from "../../../utils/normalize";
import { useBackToCloseModal } from "../../../hooks/useBackToCloseModal";
import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Sheet } from "react-modal-sheet";
import { FixedSizeList as List, ListChildComponentProps } from "react-window";

export type SelectOption = {
  element?: ReactNode;
  label: string;
  value: string | number;
  [key: string]: any;
};

type MobileSelectSheetProps = {
  options: SelectOption[];
  onSelect: (option: SelectOption) => void;
  title?: string;
  placeholder?: string;
  renderTrigger?: (open: () => void) => React.ReactNode;
  isOpen?: boolean;
  onClose?: () => void;
  filterFn?: (opt: SelectOption, keyword: string) => boolean;
  fullScreen?: boolean;
  showSearch?: boolean;
  selectedValue?: string | number | null;
};

export const MobileSelectSheet: React.FC<MobileSelectSheetProps> = ({
  options,
  onSelect,
  title = "Chọn",
  placeholder = "Tìm kiếm...",
  renderTrigger,
  isOpen,
  onClose,
  filterFn,
  fullScreen = true,
  showSearch = true,
  selectedValue = null,
}) => {
  const listRef = useRef<any>(null);

  const [internalOpen, setInternalOpen] = useState(false);
  const [search, setSearch] = useState("");

  const open = () => (isOpen === undefined ? setInternalOpen(true) : undefined);
  const close = () => {
    setSearch("");
    if (isOpen === undefined) setInternalOpen(false);
    else onClose?.();
  };

  const actualOpen = isOpen ?? internalOpen;

  useBackToCloseModal(actualOpen, close, "bottom-sheet");

  const filtered = useMemo(() => {
    const kw = search.trim().toLowerCase();
    const fn =
      filterFn ??
      ((o: SelectOption, k: string) =>
        normalizeVi(o.label).includes(normalizeVi(k)));
    return kw ? options.filter((o) => fn(o, kw)) : options;
  }, [options, search, filterFn]);

  const snapPoints = [1, 1, 0];

  function OptionRow({ index, style, data }: ListChildComponentProps) {
    const opt = data[index];
    const isSelected = data.selectedValue === opt.value;
    return (
      <button
        type="button"
        onClick={() => {
          data.onSelect(opt);
        }}
        style={{
          ...style,
          display: "flex",
          width: "100%",
          textAlign: "left",
          alignItems: "center",
          background: isSelected ? "#22c55e" : "transparent",
          color: isSelected ? "#fff" : "#111827",
          gap: 10,
          padding: "12px 4px",
          border: "none",
          borderBottom: "1px solid #f3f4f6",
          cursor: "pointer",
          fontSize: 15,
        }}
      >
        <span
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
            wordBreak: "break-word",
            lineHeight: "1.4",
            flex: 1,
            minWidth: 0,
          }}
        >
          {opt.label}
        </span>
      </button>
    );
  }

  useEffect(() => {
    if (!actualOpen) return;
    const idx = filtered.findIndex((o) => o.value === selectedValue);
    if (idx >= 0) {
      // 'center' | 'smart'
      listRef.current?.scrollToItem?.(idx, "smart");
      // Nếu version không có scrollToItem, dùng pixel:
      // listRef.current?.scrollTo?.(idx * ITEM_SIZE);
    }
  }, [actualOpen, filtered, selectedValue]);

  return (
    <>
      {renderTrigger ? (
        renderTrigger(open)
      ) : (
        <button
          type="button"
          onClick={open}
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: "1px solid #e5e7eb",
            background: "#fff",
          }}
        >
          Mở lựa chọn
        </button>
      )}

      <Sheet
        isOpen={!!actualOpen}
        onClose={close}
        snapPoints={snapPoints}
        initialSnap={1}
      >
        <Sheet.Container
          style={{ borderTopLeftRadius: 16, borderTopRightRadius: 16 }}
        >
          <Sheet.Header>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "48px 1fr 48px", // trái — giữa — phải
                alignItems: "center",
                padding: "12px 16px",
                borderBottom: "1px solid #eee",
              }}
            >
              <button
                type="button"
                onClick={close}
                aria-label="Đóng"
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()} // tránh kéo sheet khi bấm nút
                style={{
                  justifySelf: "start",
                  width: 36,
                  height: 36,
                  borderRadius: 999,
                  border: "none",
                  background: "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
                  <path
                    d="M6 6L18 18M6 18L18 6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </svg>
              </button>

              <div
                style={{ textAlign: "center", fontWeight: 600, fontSize: 16 }}
              >
                {title}
              </div>

              <div />
            </div>
          </Sheet.Header>

          <Sheet.Content>
            <div style={{ padding: "12px 16px" }}>
              {showSearch && (
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={placeholder}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid #e5e7eb",
                    outline: "none",
                    fontSize: 14,
                  }}
                />
              )}

              <div
                style={{
                  marginTop: 12,
                  maxHeight: "75vh",
                  overflowY: "auto",
                  WebkitOverflowScrolling: "touch",
                }}
              >
                {filtered.length === 0 ? (
                  <div
                    style={{
                      padding: "16px 4px",
                      color: "#6b7280",
                      fontSize: 14,
                    }}
                  >
                    Không tìm thấy kết quả
                  </div>
                ) : (
                  <List
                    ref={listRef}
                    height={Math.min(0.75 * window.innerHeight, 700)}
                    itemCount={filtered.length}
                    itemSize={48}
                    width={"100%"}
                    itemData={{
                      ...filtered,
                      onSelect: (opt: any) => {
                        onSelect(opt);
                        setSearch("");
                        close();
                      },
                      selectedValue,
                    }}
                  >
                    {OptionRow}
                  </List>
                )}
              </div>
            </div>
          </Sheet.Content>
        </Sheet.Container>
        <Sheet.Backdrop onTap={close} />
      </Sheet>
    </>
  );
};
